import { UserService } from "user";
import { Injectable, inject } from "@angular/core";
import { LocalStorageService } from "angular-2-local-storage";
import { ApiUser } from "api";
import * as uuid from 'uuid'
import { take } from "rxjs/operators";

export interface CartItem {
    uuid: string;
    type: string;
    price: number;
    recurs?: 'monthly' | 'annually';
    quantity: number;
}

export interface Cart<T extends CartItem> {
    id: string;
    email?: string;
    userUUID?: string;
    totals: {
        total: number;
        fixed: number;
        monthly: number;
        annually: number;
    };
    items: T[];
}

@Injectable()
export class CartService {
    private userService = inject(UserService);
    private localStorage = inject(LocalStorageService);

    readonly ready = this.userService.userChanged.pipe(take(1)).toPromise().then(() => {});

    get user() {
        return this.userService.user;
    }

    calculateTotals<T extends CartItem>(cart: Cart<T>) {
        cart.totals.total = cart.items.reduce((sum, item) => sum + item.price * item.quantity, 0);
        cart.totals.fixed = cart.items.filter(x => !x.recurs).reduce((sum, item) => sum + item.price * item.quantity, 0);
        cart.totals.monthly = cart.items.filter(x => x.recurs === 'monthly').reduce((sum, item) => sum + item.price * item.quantity, 0);
        cart.totals.annually = cart.items.filter(x => x.recurs === 'annually').reduce((sum, item) => sum + item.price * item.quantity, 0);
    }

    isPurchasePending(id: string): boolean {
        return this.getCart(id).items.length > 0;
    }

    getCart<T extends CartItem>(id: string): Cart<T> {
        let cart: Cart<T> = {
            items: [],
            totals: {
                total: 0,
                fixed: 0,
                monthly: 0,
                annually: 0
            },
            ...this.localStorage.get(`tyt:cart:${id}`) ?? {},
            id
        };

        if (cart.userUUID && cart.userUUID !== this.user?.uuid) {
            this.delete(cart);
            cart.items = [];
            cart.totals = { annually: 0, fixed: 0, monthly: 0, total: 0 };
        }

        return cart;
    }

    saveCart<T extends CartItem>(cart: Cart<T>) {
        cart.userUUID = this.user?.uuid;
        cart.email = this.user?.email ?? cart.email;
        this.calculateTotals(cart);
        this.localStorage.set(`tyt:cart:${cart.id}`, cart);
    }

    newItem<T extends CartItem>(type : string, data : Omit<T, 'uuid' | 'type' | 'quantity'> & { quantity?: number }): T {
        return <T>{
            quantity: 1,
            ...data,
            uuid: uuid.v4(),
            type: type,
        }
    }
    
    removeItem<T extends CartItem>(cart: Cart<T>, item: T) {
        let index = cart.items.findIndex(i => i.uuid === item.uuid);
        if (index >= 0)
            cart.items.splice(index, 1);
        this.saveCart(cart);
    }

    updateItem<T extends CartItem>(cart: Cart<T>, item: T) {
        let found = false;

        let existingItem = cart.items.find(x => x.uuid === item.uuid);

        if (existingItem)
            Object.assign(existingItem, item);
        else
            cart.items.push(item);

        this.saveCart(cart);
    }

    updateCart<T extends CartItem>(cart: Cart<T>, update: Partial<Omit<T, 'items'>>) {
        Object.assign(cart, update);
        this.saveCart(cart);
    }
    
    delete<T extends CartItem>(cart: Cart<T>) {
        cart.items = [];
        cart.email = undefined;
        delete localStorage[`tyt:cart:${cart.id}`];
    }

    clear<T extends CartItem>(cart: Cart<T>) {
        cart.items = [];
        cart.email = undefined;
        this.saveCart(cart);
    }
}